<template>
    <sidebar @close="$emit('close')" v-if="insurancePlan">
        <template v-slot:title>
            {{ insurancePlan.plan_name }}
        </template>

        <template v-slot:header>
            <div class="row mb-1">
                <div class="col">
                    <span class="text-muted">{{ company.name }} &bullet; {{ insurancePlan.plan_type_pretty }} Insurance</span>
                </div>
            </div>

            <!-- Tabs -->
            <div class="nav-tabs-container">
                <div class="nav-tabs-scroll-helper">
                    <ul class="nav nav-tabs">
                        <li class="nav-item">
                            <a class="nav-link" :class="{active: activeTab == 'main'}" href="#tabEarningContentMain" id="tabEarningLinkMain" data-toggle="tab" role="tab" aria-controls="tabEarningContentMain" :aria-selected="activeTab == 'main'">Main</a>
                        </li>
                    </ul>
                </div>
            </div>
        </template>

        <slot>
            <div class="tab-content">
                <div class="tab-pane" :class="{'show active': activeTab == 'main'}" id="tabEarningContentMain" role="tabpanel" aria-labelledby="tabEarningLinkMain">

                    <info-widget :collapsible="false">
                        <template v-slot:title>Plan Information</template>

                        <template v-slot:headerbutton>
                            <button type="button" class="btn btn-outline-primary" @click.prevent="editPlanModalOpen = true">
                                <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                                Edit
                            </button>
                        </template>

                        <slot>
                            <div class="card-body py-1 mt-1">
                                <div class="row mb-0">
                                    <div class="col">
                                        <div class="mui-textfield">
                                            <span class="textfield">{{ insurancePlan.plan_number }}</span>
                                            <label>Plan Number</label>
                                        </div>

                                        <div class="mui-textfield">
                                            <span class="textfield">{{ $filters.date(insurancePlan.open_enrollment_starts_on) }}</span>
                                            <label>Open Enrollment Start</label>
                                        </div>

                                        <div class="mui-textfield">
                                            <span class="textfield">{{ insurancePlan.waiting_period }} days</span>
                                            <label>Waiting Period</label>
                                        </div>

                                        <div class="mui-textfield">
                                            <span class="textfield">{{ insurancePlan.has_hsa ? 'Yes' : 'No' }}</span>
                                            <label>HSA?</label>
                                        </div>
                                    </div>

                                    <div class="col">
                                        <div class="mui-textfield">
                                            <span class="textfield">{{ deductionType.name }}</span>
                                            <label>Deduction Type</label>
                                        </div>

                                        <div class="mui-textfield">
                                            <span class="textfield">{{ $filters.date(insurancePlan.starts_on) }}</span>
                                            <label>Renewal Date</label>
                                        </div>

                                        <div class="mui-textfield">
                                            <span class="textfield">{{ $filters.date(insurancePlan.open_enrollment_ends_on) }}</span>
                                            <label>Open Enrollment End</label>
                                        </div>

                                        <div class="mui-textfield">
                                            <span class="textfield">{{ insurancePlan.termination_mode_pretty }}</span>
                                            <label>Termination Settings</label>
                                        </div>
                                    </div>
                                </div>

                                <h5>Billing</h5>

                                <div class="row mb-0">
                                    <div class="col">
                                        <div class="mui-textfield">
                                            <span class="textfield">{{ insurancePlan.billing_type_pretty }}</span>
                                            <label>Billing Type</label>
                                        </div>

                                        <div class="mui-textfield">
                                            <span class="textfield">{{ insurancePlan.employee_cost_covered }}%</span>
                                            <label>Employee Cost Covered by Employer</label>
                                        </div>

                                        <div class="mui-textfield" v-if="insurancePlan.billing_type != 'age-band'">
                                            <span class="textfield">{{ insurancePlan.dependent_cost_covered }}%</span>
                                            <label>Dependent Cost Covered by Employer</label>
                                        </div>
                                    </div>

                                    <div class="col" v-if="insurancePlan.billing_type == 'age-band'">
                                        <div class="mui-textfield">
                                            <span class="textfield">{{ insurancePlan.age_determination_method_pretty }}</span>
                                            <label>Age Band Determination</label>
                                        </div>

                                        <div class="mui-textfield" v-if="insurancePlan.age_determination_method == 'explicit'">
                                            <span class="textfield">{{ $filters.date(insurancePlan.explicit_age_band_date) }}</span>
                                            <label>Age Band Determination Date</label>
                                        </div>

                                    </div>
                                </div>

                            </div>
                        </slot>
                    </info-widget>

                    <cost-sidebar-widget :client="client" :insurance-plan="insurancePlan" v-if="insurancePlan" :start-expanded="true" />

                    <info-widget :collapsible="false">
                        <template v-slot:title>Positions ({{ insurancePlan.insurance_plan_positions?.length || 0 }})</template>

                        <template v-slot:headerbutton>
                            <button type="button" class="btn btn-outline-primary" @click.prevent="editPositionsModalOpen = true">
                                <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                                Edit
                            </button>
                        </template>

                        <slot>
                            <div v-if="insurancePlan.insurance_plan_positions?.length" class="p-1">
                                <table class="table css-table widget-table table-fixed">
                                    <thead>
                                        <tr>
                                            <th>Positions</th>
                                        </tr>
                                    </thead>
                                    <tbody v-for="ipp in insurancePlan.insurance_plan_positions" :key="ipp.id">
                                        <tr>
                                            <td class="truncate-wrapper">
                                                <span>{{ ipp.position.terminal_value }}</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="info-widget-empty p-2" v-else>
                                This insurance plan is not associated with any positions.
                            </div>
                        </slot>
                    </info-widget>
                </div>
            </div>
        </slot>

        <edit-plan-modal
            v-if="editPlanModalOpen"
            :insurance-plan="insurancePlan"
            :client="client"
            :deduction-types="deductionTypes"
            @updated="onUpdate"
            @close="editPlanModalOpen = false"
        />

        <positions-modal
            v-if="editPositionsModalOpen"
            :insurance-plan="insurancePlan"
            :client="client"
            @updated="onUpdate"
            @close="editPositionsModalOpen = false"
        />
    </sidebar>
</template>

<script>
import Sidebar from '@/components/Sidebar'
import InfoWidget from '@/components/InfoWidget'
import ButtonRow from '@/components/ButtonRow'
import EditPlanModal from './EditPlanModal'
import PositionsModal from './PositionsModal'
import CostSidebarWidget from './CostSidebarWidget'

export default {
    props: ['insurancePlanId', 'client', 'tab', 'deductionTypes', ],
    components: {Sidebar, InfoWidget, ButtonRow, EditPlanModal, CostSidebarWidget, PositionsModal, },
    computed: {
        company() {
            return this.client.companies.find(c => c.id == this.insurancePlan.company_id)
        },
        deductionType() {
            return this.deductionTypes.find(d => d.id == this.insurancePlan.deduction_type_id)
        },
    },
    data() {
        return {
            insurancePlan: null,
            activeTab: this.tab || 'main',
            editPlanModalOpen: false,
            editPositionsModalOpen: false,
        }
    },
    watch: {
        insurancePlanId() {
            this.getData()
        }
    },
    mounted() {
        this.getData()
    },
    methods: {
        getData() {
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/clients/${this.client.id}/settings/insurance-plans/${this.insurancePlanId}`).then(resp => {
                this.insurancePlan = resp
                this.$store.dispatch('STOP_LOADING')
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        onUpdate(obj) {
            if (obj) {
                Object.assign(this.insurancePlan, obj)
                this.$emit('updated')
            }
            else {
                this.getData()
            }
        },
    },
}
</script>

